import React, { useState, useEffect, useContext } from 'react';
import { formatUnits } from 'ethers';
import { useTranslation } from 'react-i18next';
import { NetworkContext } from '../../../contexts/NetworkContext';
import { getLogoUrl, DEFAULT_TOKEN_LOGO } from '../../../utils/logoCacheUtils';

const TokenItem = ({ tokenObj, tokenPrice, tokenLogo, priceChange, onClick }) => {
  const { t, i18n } = useTranslation();
  const { token, value } = tokenObj;
  const { currentChain } = useContext(NetworkContext);
  const [logoSrc, setLogoSrc] = useState(DEFAULT_TOKEN_LOGO);
  
  // Get current locale from i18n
  const currentLocale = i18n.language || 'en';
  
  // Format token balance
  let rawVal = value;
  if (token.decimals) {
    try {
      rawVal = formatUnits(value, Number(token.decimals));
    } catch {
      rawVal = "0";
    }
  }
  
  const balNum = parseFloat(rawVal);
  const balStr = balNum.toLocaleString(currentLocale, { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 5 
  });
  
  // Calculate USD value
  const usdVal = balNum * tokenPrice;
  const usdStr = usdVal > 0
    ? usdVal.toLocaleString(currentLocale, { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2,
        style: 'currency',
        currency: 'USD'
      })
    : '';
  
  // Determine color for price change
  const priceChangeColor = priceChange > 0 
    ? 'var(--success)' 
    : priceChange < 0 
      ? 'var(--error)' 
      : 'var(--text-tertiary)';
  
  // Format price change
  const priceChangeStr = priceChange !== undefined && priceChange !== null
    ? `${priceChange > 0 ? '+' : ''}${priceChange.toFixed(2)}%`
    : '';
  
  // Load logo from cache when component mounts or when token/logo changes
  useEffect(() => {
    if (token && token.address && currentChain) {
      // Get logo from cache or use the provided tokenLogo
      const cachedLogo = getLogoUrl(token.address, currentChain.key, tokenLogo);
      setLogoSrc(cachedLogo);
    }
  }, [token, tokenLogo, currentChain]);
  
  // Handle image load error
  const handleImageError = () => {
    setLogoSrc(DEFAULT_TOKEN_LOGO);
  };
  
  return (
    <div className="token-container" onClick={onClick}>
      <div className="token-row three-column">
        {/* Column 1: Logo */}
        <img 
          src={logoSrc} 
          alt={token.symbol} 
          className="token-logo"
          onError={handleImageError} 
        />
        
        {/* Column 2: Token name and balance */}
        <div className="token-name-balance">
          <span className="token-name">
            {token.name} ({token.symbol})
          </span>
          <div className="token-balance">
            Balance: {balStr}
          </div>
        </div>
        
        {/* Column 3: Price and price change */}
        <div className="token-price-info">
          <span className="token-usd">{usdStr}</span>
          {priceChangeStr && (
            <span className="token-price-change" style={{ color: priceChangeColor }}>
              {priceChangeStr}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default TokenItem;